// @ts-check
/**
 * Cart Channel Attribute + DY Consent Attributes
 * ─────────────────────────────────────────────────────────────
 * Ensures the Shopify cart always carries:
 *
 *   channel: "web"
 *
 * And when DY cookies are available:
 *
 *   dy: JSON.stringify({
 *     active_consent_accepted: <boolean>,
 *     dyid:        <string>,   // only when _dyid cookie exists
 *     dyid_server: <string>,   // only when _dyid_server cookie exists
 *     session:     <string>,   // only when _dyjsession cookie exists
 *   })
 *
 * DY cookie strategy:
 *   - Polls every 200ms (max 3s) waiting for DY cookies to be set.
 *   - If cookies are found before timeout  → includes dy attribute.
 *   - If cookies never appear              → skips dy entirely, only writes channel.
 *   - Never writes empty/null DY values to the cart.
 *
 * Execution strategy:
 *   - Waits for 'theme:init' (Horizon) before running.
 *   - Runs exactly once via a 'ran' guard.
 *   - Diffs desired vs existing cart attributes — only POSTs when
 *     something is missing or changed.
 * ─────────────────────────────────────────────────────────────
 */

(function CartChannelAttribute() {
  'use strict';

  // ── Guard: run only once per page ──────────────────────────────
  if (window.__cartChannelAttributeInit) return;
  window.__cartChannelAttributeInit = true;

  // ── Constants ───────────────────────────────────────────────────
  var CHANNEL_KEY        = 'channel';
  var CHANNEL_VALUE      = 'web';
  var DY_POLL_INTERVAL   = 200;  // ms between each DY cookie poll
  var DY_POLL_TIMEOUT    = 3000; // ms max wait for DY cookies
  var THEME_TIMEOUT_MS   = 5000; // ms fallback if theme:init never fires

  // ── Cookie helper ───────────────────────────────────────────────
  /**
   * Reads a cookie value by name.
   * Returns empty string (not null) so callers skip null-checks.
   *
   * @param {string} name
   * @returns {string}
   */
  function getCookie(name) {
    var escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    var match   = document.cookie.match(
      new RegExp('(?:^|;\\s*)' + escaped + '=([^;]*)')
    );
    return match ? decodeURIComponent(match[1]) : '';
  }

  // ── Consent detection ───────────────────────────────────────────
  /**
   * Returns true when the visitor has accepted the cookie banner.
   *
   * @returns {boolean}
   */
  function hasConsentAccepted() {
    // 1. Shopify native Customer Privacy API
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    if (cp) {
      if (typeof cp.analyticsProcessingAllowed === 'function' && cp.analyticsProcessingAllowed()) return true;
      if (typeof cp.marketingAllowed           === 'function' && cp.marketingAllowed())           return true;
    }

    // 2. CookieYes
    var cy = getCookie('cookieyes-consent');
    if (cy && cy.indexOf('accepted:yes') !== -1) return true;

    // 3. OneTrust
    if (getCookie('OptanonAlertBoxClosed') !== '') return true;

    // 4. Generic Shopify cookie consent
    if (getCookie('cookiesAccepted') === 'true') return true;

    return false;
  }

  // ── DY cookie poller ────────────────────────────────────────────
  /**
   * Polls for DY cookies up to DY_POLL_TIMEOUT ms.
   * Resolves with the cookie values when found, or null if timed out.
   *
   * @returns {Promise<{ dyid: string, dyid_server: string, session: string } | null>}
   */
  function waitForDYCookies() {
    return new Promise(function (resolve) {
      var elapsed   = 0;
      var interval  = null;

      function check() {
        var dyid       = getCookie('_dyid');
        var dyidServer = getCookie('_dyid_server');
        var dySession  = getCookie('_dyjsession') || window.DY.jsession;
        console.log("DY waitForDYCookies", dyid, dyidServer, dySession);

        // At least dyid must be present to consider DY ready
        if (dyid !== '') {
          clearInterval(interval);
          resolve({ dyid: dyid, dyid_server: dyidServer, session: dySession });
          return;
        }

        elapsed += DY_POLL_INTERVAL;
        if (elapsed >= DY_POLL_TIMEOUT) {
          clearInterval(interval);
          resolve(null); // DY never became available — skip dy attribute
        }
      }

      interval = setInterval(check, DY_POLL_INTERVAL);
      check(); // run immediately before first interval
    });
  }

  // ── Build desired cart attributes ───────────────────────────────
  /**
   * Builds the attribute map we want on the cart.
   * Only includes 'dy' when DY cookies are actually available.
   *
   * @param {{ dyid: string, dyid_server: string, session: string } | null} dyCookies
   * @returns {Record<string, string>}
   */
  function buildDesiredAttributes(dyCookies) {
    var attrs = {};

    attrs[CHANNEL_KEY] = CHANNEL_VALUE;

    if (dyCookies !== null) {
      var dy = {
        active_consent_accepted: hasConsentAccepted(),
        dyid:        dyCookies.dyid,
        dyid_server: dyCookies.dyid_server,
        session:     dyCookies.session,
      };
      attrs['dy'] = JSON.stringify(dy);
    }

    return attrs;
  }

  // ── Cart fetch ──────────────────────────────────────────────────
  /**
   * @returns {Promise<{ attributes?: Record<string, unknown> } | null>}
   */
  function fetchCart() {
    return fetch('/cart.js', {
      method:      'GET',
      credentials: 'same-origin',
      cache:       'no-store',
      headers:     { Accept: 'application/json' },
    })
      .then(function (res) {
        if (!res.ok) return null;
        return res.json();
      })
      .catch(function () { return null; });
  }

  // ── Diff ────────────────────────────────────────────────────────
  /**
   * Returns only the attributes that are missing or different in the cart.
   * Empty object = no write needed.
   *
   * @param {Record<string, string>}                           desired
   * @param {{ attributes?: Record<string, unknown> } | null}  cart
   * @returns {Record<string, string>}
   */
  function diffAttributes(desired, cart) {
    var existing = (cart && cart.attributes) ? cart.attributes : {};
    var diff     = {};
    var keys     = Object.keys(desired);

    for (var i = 0; i < keys.length; i++) {
      var key   = keys[i];
      var value = desired[key];
      var current = existing[key] !== undefined ? String(existing[key]).trim() : '';
      if (current !== value) {
        diff[key] = value;
      }
    }

    return diff;
  }

  // ── Cart update ─────────────────────────────────────────────────
  /**
   * @param {Record<string, string>} attributes
   * @returns {Promise<void>}
   */
  function applyAttributes(attributes) {
    return fetch('/cart/update.js', {
      method:      'POST',
      credentials: 'same-origin',
      headers:     { 'Content-Type': 'application/json' },
      body:        JSON.stringify({ attributes: attributes }),
    })
      .catch(function () { /* silent — never break the page */ });
  }

  // ── Core run ────────────────────────────────────────────────────
  /**
   * Waits for DY cookies, builds attributes, diffs, writes if needed.
   *
   * @returns {Promise<void>}
   */
  function run() {
    return waitForDYCookies()
      .then(function (dyCookies) {
        return fetchCart().then(function (cart) {
          var desired = buildDesiredAttributes(dyCookies);
          var diff    = diffAttributes(desired, cart);

          if (Object.keys(diff).length === 0) return; // nothing changed
          return applyAttributes(diff);
        });
      })
      .catch(function () { /* silent */ });
  }

  // ── Theme ready gate ────────────────────────────────────────────
  var ran          = false;
  var themeTimer   = null;

  function tryRun() {
    if (ran) return;
    ran = true;

    if (themeTimer !== null) {
      clearTimeout(themeTimer);
      themeTimer = null;
    }

    run();
  }

  // Wait for Horizon theme:init before running
  document.addEventListener('theme:init', tryRun, { once: true });

  // Safety fallback — if theme:init never fires
  themeTimer = setTimeout(tryRun, THEME_TIMEOUT_MS);

})();
